import React from "react";
import { Helmet } from "react-helmet";
import { graphql } from "gatsby";

import Layout from "../../components/Layout";
import WithI18next from "../../components/WithI18next";
import HomeLayout from "../../layouts/HomeLayout";

import "../../components/partials/home-page/home.scss";

const HomePage = ({ data, pageContext, t, lang }) => {
  return (
    <Layout
      title={t("home.seo.title")}
      description={t("home.seo.description")}
      limeSignUpButton={true}
    >
      <Helmet>
        <body className="homepage" />
      </Helmet>
      <div id="home">
        <HomeLayout data={data} isIndo={pageContext.isIndo} t={t} />
      </div>
    </Layout>
  );
};

export default WithI18next()(HomePage);

export const query = graphql`
  query (
    $wpLocale: String!
    $wpGlobalConfig: String!
    $isIndo: Boolean = false
  ) {
    ...MediaLinks
    ...GetOnBoardContent
    homePage: allWpComponentItem(
      filter: {
        acfComponents: { cApplySection: { eq: "home_page" } }
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          title
          slug
          content
        }
      }
    }
    heroContent: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: "home" } }
      }
    ) {
      edges {
        node {
          acfHeroSections {
            hsMainHeading
            hsSubHeading
            hsMainText
            hsButtonText
            hsButtonLink
          }
        }
      }
    }
    heroCryptoRev: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: "home-crypto-rev" } }
      }
    ) {
      nodes {
        acfHeroSections {
          hsMainHeading
          hsSubHeading
          hsMainText
          hsButtonText
          hsButtonLink
        }
      }
    }
    heroImage: file(relativePath: { eq: "images/zipmex-hero.svg" }) {
      publicURL
    }
    promos: allWpPromotionItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [$wpGlobalConfig] } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          title
          slug
          acfPromotions {
            proUrl
            proImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 450, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      }
    }
    digitalAssetsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [$wpGlobalConfig] } }
        acfPartials: { pPartialId: { eq: "digital-assets" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }
    coinInterestRates: allWpCoinInterestRatesItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: ".all" } }
        language: { locale: { eq: "en_US" } }
        acfCoinInterestRates: { cirShowOnHomepage: { eq: "1" } }
      }
      sort: { order: ASC, fields: acfCoinInterestRates___cirOrder }
    ) {
      edges {
        node {
          acfCoinInterestRates {
            cirCoinIcon {
              localFile {
                publicURL
              }
              mediaDetails {
                height
                width
              }
            }
            zipupInterateRateV2 {
              vip4 {
                rate
              }
            }
          }
        }
      }
    }
    whyZipmexContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [$wpGlobalConfig] } }
        acfPartials: { pPartialId: { eq: "why-zipmex" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }
    personalizeContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [$wpGlobalConfig] } }
        acfPartials: {
          pPartialId: { eq: "personalize-your-investment-portfolio" }
        }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pElementId
            pHeading
            pContent
            pIcon {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 525, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      }
    }
    imageBuyBtc: file(relativePath: { eq: "images/buy-btc.svg" }) {
      publicURL
    }
    fiatDepositsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "fiat-deposits-made-easy" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) @include(if: $isIndo) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }
    howDepositContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "how-deposit-money" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) @include(if: $isIndo) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }
    iconInfo: file(relativePath: { eq: "images/icons/info-alt.svg" }) {
      publicURL
    }

    opinions: allWpPost(
      filter: {
        categories: { nodes: { elemMatch: { name: { regex: "/Opinions/" } } } }
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
      }
    ) {
      edges {
        node {
          title
          excerpt
          slug
          date(formatString: "MMMM DD, YYYY")
          featuredImage {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 450, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      }
    }
  }
`;
